import { inject, Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { ILocation } from '../interfaces/location.interface';
import { firstValueFrom } from 'rxjs';
import { SystemElementsEnum } from '../enums/system-elements.enum';
import { Utils } from '../tools/utils';
import { IRoom } from '../interfaces/room.interface';
import { IAlarm } from '../interfaces/alarm.interface';
import { SignalRLocationMessage } from '../interfaces/signalR-location.interface';
import { isNullOrUndefined } from 'is-what';

@Injectable({ providedIn: 'root' })
export class LocationsService {
  apiBaseUrl: string = environment.apiBaseUrl;
  private http = inject(HttpClient);

  private locationsSignal = signal<ILocation[]>([]);
  readonly locations = this.locationsSignal.asReadonly();

  private roomsSignal = signal<IRoom>({} as IRoom);
  readonly rooms = this.roomsSignal.asReadonly();

  private alarmsSignal = signal<IAlarm>({} as IAlarm);
  readonly alarms = this.alarmsSignal.asReadonly();

  private locationChangedSignal = signal<SignalRLocationMessage | null>(null);
  readonly locationChanged = this.locationChangedSignal.asReadonly();

  async getLocations(): Promise<void> {
    const request$ = this.http.get<ILocation[]>(
      `${this.apiBaseUrl}client/locations`,
      {
        observe: 'response',
      },
    );
    const result = await firstValueFrom(request$);
    let data = result.body ?? [];

    data.forEach((location) => {
      const sysElements = [
        {
          key: SystemElementsEnum.Rooms,
          value: location.totalRooms,
        },
        {
          key: SystemElementsEnum.N2Generators,
          value: null!,
        },
        {
          key: SystemElementsEnum.CO2Adsorbers,
          value: null!,
        },
        {
          key: SystemElementsEnum.C2H4Combustors,
          value: null!,
        },
        {
          key: SystemElementsEnum.MeasuringSystems,
          value: null!,
        },
        {
          key: SystemElementsEnum.RefrigerationPlants,
          value: null!,
        },
      ];

      location.sysElements = sysElements;
    });

    data = Utils.sortByProperty(data, 'locationName', 'ASC');

    this.locationsSignal.set(data as ILocation[]);
  }

  async getRoomsByLocation(locationId: string): Promise<void> {
    const request$ = this.http.get<IRoom>(
      `${this.apiBaseUrl}client/locations/${locationId}/rooms`,
      {
        observe: 'response',
      },
    );
    const result = await firstValueFrom(request$);
    const roomData = result.body ?? ({} as IRoom);

    await this.getAlarmsByLocation(locationId);
    const alarmData = this.alarms();

    if (roomData.rooms && alarmData.alarms) {
      roomData.rooms.forEach((room) => {
        const alarms = alarmData.alarms.find(
          (alarm) =>
            alarm.element === 'ROOM' && alarm.elementID === room.roomId,
        );
        room.alarms = alarms?.alarms ?? [];
      });
    }

    this.roomsSignal.set(roomData);
  }

  async getAlarmsByLocation(locationId: string): Promise<void> {
    const request$ = this.http.get<IAlarm>(
      `${this.apiBaseUrl}client/locations/${locationId}/alarms`,
      {
        observe: 'response',
      },
    );
    const result = await firstValueFrom(request$);
    this.alarmsSignal.set(result.body ?? ({} as IAlarm));
  }

  refreshLocation(message: SignalRLocationMessage | null): void {
    if (isNullOrUndefined(message)) {
      this.locationChangedSignal.set(null);
      return;
    }
    this.locationChangedSignal.set({ ...message });
  }

  async dismissAllAlarms(locationId: string): Promise<boolean> {
    const request$ = this.http.delete<boolean>(
      `${this.apiBaseUrl}client/locations/${locationId}/alarms`,
      {
        observe: 'response',
      },
    );
    const result = await firstValueFrom(request$);

    return result.body ?? false;
  }
}
